import { useMemo } from "react";
import { FieldPath, FieldValues, Message, RegisterOptions, UseControllerProps, ValidationRule, ValidationValue } from "react-hook-form";
import { IntlShape, MessageDescriptor, useIntl } from "react-intl";

type TranslatedValidationValueMessage<TValidationValue extends ValidationValue = ValidationValue> = {
    value: TValidationValue;
    message: MessageDescriptor | string;
};

type TranslatedValidationRule<TValidationValue extends ValidationValue = ValidationValue> = TranslatedValidationValueMessage<TValidationValue>;

type BaseTranslatedRegisterOptions = Partial<{
    required: MessageDescriptor | TranslatedValidationRule<boolean>;
    min: TranslatedValidationRule<number | string>;
    max: TranslatedValidationRule<number | string>;
    maxLength: TranslatedValidationRule<number | string>;
    minLength: TranslatedValidationRule<number | string>;
    pattern: TranslatedValidationRule<RegExp>;
}>;
export type TranslatedRegisterOptions<TFieldValues extends FieldValues = FieldValues, TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> =
    BaseTranslatedRegisterOptions
    & Pick<RegisterOptions<TFieldValues, TFieldName>, "valueAsNumber" | "valueAsDate" | "value" | "setValueAs" | "shouldUnregister" | "onChange" | "onBlur" | "disabled" | "deps" | "validate">;
export type TranslatedUseControllerOptions<TFieldValues extends FieldValues = FieldValues, TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> =
    BaseTranslatedRegisterOptions
    & Omit<UseControllerProps<TFieldValues, TFieldName>["rules"], "required" | "min" | "max" | "maxLength" | "minLength" | "pattern">;

export function useTranslatedValidations<TFieldValues extends FieldValues, TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>(
    options: TranslatedRegisterOptions<TFieldValues, TFieldName> | undefined
): RegisterOptions<TFieldValues, TFieldName> | undefined;
export function useTranslatedValidations<TFieldValues extends FieldValues, TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>(
    options: TranslatedUseControllerOptions<TFieldValues, TFieldName> | undefined
): Required<UseControllerProps<TFieldValues, TFieldName>>["rules"] | undefined;
export function useTranslatedValidations<TFieldValues extends FieldValues, TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>(
    options: TranslatedRegisterOptions<TFieldValues, TFieldName> | TranslatedUseControllerOptions<TFieldValues, TFieldName> | undefined
): RegisterOptions<TFieldValues, TFieldName> | Required<UseControllerProps<TFieldValues, TFieldName>>["rules"] | undefined {
    const { formatMessage } = useIntl();

    const registerOptions = useMemo<RegisterOptions<TFieldValues, TFieldName> | undefined>(() => {
        if (!options) {
            return undefined;
        }

        const {
            required,
            min,
            max,
            maxLength,
            minLength,
            pattern,
            ...rest
        } = options;

        return {
            required: convertRequired(required, formatMessage),
            min: convertTranslatedValidationValueMessage(min, formatMessage),
            max: convertTranslatedValidationValueMessage(max, formatMessage),
            maxLength: convertTranslatedValidationValueMessage(maxLength, formatMessage),
            minLength: convertTranslatedValidationValueMessage(minLength, formatMessage),
            pattern: convertTranslatedValidationValueMessage(pattern, formatMessage),
            ...rest,
        } as RegisterOptions<TFieldValues, TFieldName>;
    }, [formatMessage, options]);

    return registerOptions;
}

function convertRequired(
    required: MessageDescriptor | TranslatedValidationRule<boolean> | undefined,
    formatMessage: IntlShape["formatMessage"]
): Message | ValidationRule<boolean> | undefined {
    if (!required) {
        return undefined;
    }

    if (isMessageDescriptor(required)) {
        return formatMessage(required);
    }

    return convertTranslatedValidationValueMessage(required, formatMessage);
}

function convertTranslatedValidationValueMessage<TValidationValue extends ValidationValue = ValidationValue>(
    rule: TranslatedValidationRule<TValidationValue> | undefined,
    formatMessage: IntlShape["formatMessage"]
): ValidationRule<TValidationValue> | undefined {
    if (!rule) {
        return undefined;
    }

    const {
        message,
        value,
        ...rest
    } = rule;

    if (typeof message === "string") {
        return {
            message,
            value,
            ...rest
        }
    }

    return {
        message: typeof value === "string" || typeof value === "number"
            ? formatMessage(message, { value })
            : formatMessage(message),
        value,
        ...rest,
    };
}

function isMessageDescriptor<TValidationValue extends ValidationValue = ValidationValue>(rule: MessageDescriptor | TranslatedValidationRule<TValidationValue> | undefined): rule is MessageDescriptor {
    return (rule as MessageDescriptor)?.id !== undefined;
}
